import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Paper, Stack, TextField, Typography } from '@mui/material';

import {
  buildLevelToTableMap,
  buildTableToLevelMap,
  flattenLevels,
  getOrphanTables,
  hasHierarchy,
  renameColumn,
  type SchemaDefinitionLike,
} from '../../lib/schemaTopology';
import HierarchyTreeNav, {
  type NavSelection,
} from '../schema-view/HierarchyTreeNav';
import LevelTableView from '../schema-view/LevelTableView';
import {
  updateLevelAtPath,
  type OutputLevel,
  type OutputStructure,
} from '../../lib/outputStructure';
// Phase 3 retired the standalone value-catalog construct; closed-domain
// constraints live inline on ColumnDef.validation_rules and the editor
// surfaces them through ValidationRulesDialog rather than a separate
// catalog editor.

export interface UnifiedSchemaEditorProps {
  /** Current working copy of `definition`. */
  editPayload: any;
  /** Replace the working copy. The dialog's footer Save/Cancel reads
   *  from this state. */
  onChange: (next: any) => void;
  /** Open the validation-rules dialog for a specific column. */
  onOpenRulesEditor: (tableName: string, columnName: string) => void;
}

/**
 * Editable variant of the unified schema browser. Hierarchy on the
 * left (inline-rename levels, add/remove), the selected level's table
 * on the right (rename / type / description / fixed / inline allowed-
 * value chips per column, validation_rules editor for typed rules).
 *
 * Owns no persistent state — `editPayload` + `onChange` is the
 * authoritative working copy. Local UI state (selection, dirty
 * markers) lives in this component.
 */
const UnifiedSchemaEditor: React.FC<UnifiedSchemaEditorProps> = ({
  editPayload,
  onChange,
  onOpenRulesEditor,
}) => {
  const definition: SchemaDefinitionLike = editPayload || {};
  const tableNames = useMemo(() => Object.keys(definition?.tables || {}), [definition]);
  const levelToTable = useMemo(() => buildLevelToTableMap(definition), [definition]);
  const tableToLevel = useMemo(() => buildTableToLevelMap(definition), [definition]);
  const orphanTables = useMemo(() => getOrphanTables(definition), [definition]);
  const hasTree = hasHierarchy(definition);
  const flat = useMemo(
    () => (hasTree ? flattenLevels(definition.output_structure as OutputStructure) : []),
    [hasTree, definition],
  );

  const [selection, setSelection] = useState<NavSelection>(() => {
    if (hasTree && flat.length > 0) return { kind: 'level', pathKey: flat[0].pathKey };
    if (tableNames.length > 0) return { kind: 'table', tableName: tableNames[0] };
    return null;
  });

  useEffect(() => {
    if (selection?.kind === 'level' && !levelToTable[selection.pathKey] && flat.length > 0) {
      setSelection({ kind: 'level', pathKey: flat[0].pathKey });
    } else if (selection?.kind === 'table' && !definition?.tables?.[selection.tableName]) {
      if (tableNames.length > 0) {
        setSelection({ kind: 'table', tableName: tableNames[0] });
      } else {
        setSelection(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [definition]);

  // ---- Resolve selection to active table/level/breadcrumb ----
  let activeTable: string | null = null;
  let activeLevel: OutputLevel | null = null;
  let activePathKey: string | null = null;
  let breadcrumb: string[] = [];
  if (selection?.kind === 'level') {
    activePathKey = selection.pathKey;
    activeTable = levelToTable[selection.pathKey] || null;
    const entry = flat.find((e) => e.pathKey === selection.pathKey);
    activeLevel = entry?.level || null;
    breadcrumb = entry?.path || [];
  } else if (selection?.kind === 'table') {
    activeTable = selection.tableName;
    breadcrumb = tableToLevel[selection.tableName] || [];
    if (breadcrumb.length > 0) {
      activePathKey = breadcrumb.join('/');
      const entry = flat.find((e) => e.pathKey === activePathKey);
      activeLevel = entry?.level || null;
    }
  }

  // ---- Mutators (all return a new definition; setState through onChange) ----

  const patchLevel = (pathKey: string, patch: Partial<OutputLevel>): void => {
    if (!definition.output_structure) return;
    const path = pathKey.split('/');
    const nextStructure = updateLevelAtPath(
      definition.output_structure as OutputStructure,
      path,
      (lvl) => ({ ...lvl, ...patch }),
    );
    onChange({ ...editPayload, output_structure: nextStructure });
  };

  const handleLevelRename = (pathKey: string, nextName: string): void => {
    if (!nextName.trim()) return;
    patchLevel(pathKey, { element_name: nextName.trim() });
  };

  const handleAddChildLevel = (parentPathKey: string | null): void => {
    const blankLevel: OutputLevel = {
      element_name: 'NewElement',
      key_column: '',
      children: [],
    };
    const struct = (definition.output_structure as OutputStructure) || {
      format: 'xml',
      levels: [],
    };
    let nextStructure: OutputStructure;
    if (!parentPathKey) {
      nextStructure = { ...struct, levels: [...(struct.levels || []), blankLevel] };
    } else {
      const path = parentPathKey.split('/');
      nextStructure = updateLevelAtPath(struct, path, (lvl) => ({
        ...lvl,
        children: [...(lvl.children || []), blankLevel],
      }));
    }
    onChange({ ...editPayload, output_structure: nextStructure });
  };

  const handleRemoveLevel = (pathKey: string, siblingIndex: number): void => {
    const struct = definition.output_structure as OutputStructure | undefined;
    if (!struct) return;
    if (!confirm(`Remove element "${pathKey.split('/').pop()}" and its children? Tables that were under it will move to "Other tables" but their column data is preserved.`)) return;
    const path = pathKey.split('/');
    const removeAtPath = (
      siblings: OutputLevel[],
      depth: number,
    ): OutputLevel[] => {
      const target = path[depth];
      if (depth === path.length - 1) {
        return siblings.filter((_, i) => i !== siblingIndex);
      }
      return siblings.map((s) =>
        s.element_name === target
          ? { ...s, children: removeAtPath(s.children || [], depth + 1) }
          : s,
      );
    };
    const nextStructure: OutputStructure = {
      ...struct,
      levels: removeAtPath(struct.levels || [], 0),
    };
    onChange({ ...editPayload, output_structure: nextStructure });
  };

  // ---- Table-level mutators ----

  const patchTable = (tableName: string, field: string, value: any): void => {
    const tables = { ...(definition.tables || {}) };
    if (!tables[tableName]) return;
    tables[tableName] = { ...tables[tableName], [field]: value };
    onChange({ ...editPayload, tables });
  };

  // ---- Column-level mutators ----

  const patchColumn = (tableName: string, colIndex: number, field: string, value: any): void => {
    const table = definition.tables?.[tableName];
    if (!table) return;
    const tables = { ...definition.tables };
    let nextTable = { ...table };
    const rawCols = table.columns;

    // Renames cascade across FK refs and level key_columns.
    if (field === 'name' && Array.isArray(rawCols)) {
      const oldName = rawCols[colIndex]?.name;
      if (oldName && value && oldName !== value) {
        const nextDef = renameColumn(definition as any, tableName, oldName, value);
        onChange({ ...editPayload, ...nextDef });
        return;
      }
    }

    if (Array.isArray(rawCols)) {
      const next = rawCols.slice();
      const cur = next[colIndex] || {};
      if (field === 'type') {
        next[colIndex] = { ...cur, type: value, data_type: value };
      } else if (field === 'primary_key') {
        next[colIndex] = { ...cur, primary_key: !!value, is_primary_key: !!value };
        nextTable.primary_keys = next
          .filter((c: any) => c?.primary_key || c?.is_primary_key)
          .map((c: any) => c?.name)
          .filter(Boolean);
      } else {
        next[colIndex] = { ...cur, [field]: value };
      }
      nextTable.columns = next;
    } else if (rawCols && typeof rawCols === 'object') {
      const keys = Object.keys(rawCols as any);
      const targetKey = keys[colIndex];
      if (!targetKey) return;
      const nextCols: Record<string, any> = { ...(rawCols as any) };
      const cur = nextCols[targetKey] || {};
      if (field === 'name' && value && value !== targetKey) {
        // Rename dict key.
        const nextDef = renameColumn(definition as any, tableName, targetKey, value);
        onChange({ ...editPayload, ...nextDef });
        return;
      }
      if (field === 'type') {
        nextCols[targetKey] = { ...cur, type: value, data_type: value };
      } else if (field === 'primary_key') {
        nextCols[targetKey] = { ...cur, primary_key: !!value, is_primary_key: !!value };
        nextTable.primary_keys = Object.entries(nextCols)
          .filter(([, c]) => (c as any)?.primary_key || (c as any)?.is_primary_key)
          .map(([k]) => k);
      } else {
        nextCols[targetKey] = { ...cur, [field]: value };
      }
      nextTable.columns = nextCols;
    }

    tables[tableName] = nextTable;
    onChange({ ...editPayload, tables });
  };

  const removeColumn = (tableName: string, colIndex: number): void => {
    const table = definition.tables?.[tableName];
    if (!table?.columns) return;
    const tables = { ...definition.tables };
    if (Array.isArray(table.columns)) {
      tables[tableName] = {
        ...table,
        columns: table.columns.filter((_col: unknown, i: number) => i !== colIndex),
      };
    } else {
      const keys = Object.keys(table.columns);
      const key = keys[colIndex];
      if (!key) return;
      const next = { ...table.columns };
      delete next[key];
      tables[tableName] = { ...table, columns: next };
    }
    onChange({ ...editPayload, tables });
  };

  const addColumn = (tableName: string): void => {
    const table = definition.tables?.[tableName];
    if (!table) return;
    const tables = { ...definition.tables };
    const blank = { name: 'new_column', data_type: 'VARCHAR', type: 'VARCHAR' };
    if (Array.isArray(table.columns)) {
      tables[tableName] = { ...table, columns: [...table.columns, blank] };
    } else {
      const next = { ...(table.columns || {}), new_column: { data_type: 'VARCHAR', type: 'VARCHAR' } };
      tables[tableName] = { ...table, columns: next };
    }
    onChange({ ...editPayload, tables });
  };

  // ---- Render ----

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ px: 2, pt: 2 }}>
        <TextField
          label="Schema description"
          fullWidth
          multiline
          maxRows={2}
          value={editPayload?.description || ''}
          onChange={(e) => onChange({ ...editPayload, description: e.target.value })}
          placeholder="High-level description of this schema's purpose"
          size="small"
        />
      </Box>

      {tableNames.length === 0 && !hasTree ? (
        <Alert severity="info" sx={{ m: 2 }}>
          This schema has no tables yet. Use Annotate Schema or paste a definition in the JSON tab.
        </Alert>
      ) : (
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          sx={{ p: 2, flex: 1, minHeight: 0 }}
        >
          <HierarchyTreeNav
            outputStructure={definition?.output_structure}
            tableNames={tableNames}
            orphanTables={orphanTables}
            levelToTable={levelToTable}
            selected={selection}
            onSelect={setSelection}
            mode="edit"
            onLevelRename={handleLevelRename}
            onAddChildLevel={handleAddChildLevel}
            onRemoveLevel={handleRemoveLevel}
          />
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <LevelTableView
              definition={definition}
              tableName={activeTable}
              level={activeLevel}
              breadcrumb={breadcrumb}
              mode="edit"
              onTablePatch={
                activeTable ? (field, value) => patchTable(activeTable as string, field, value) : undefined
              }
              onColumnPatch={
                activeTable
                  ? (colIdx, field, value) => patchColumn(activeTable as string, colIdx, field, value)
                  : undefined
              }
              onAddColumn={activeTable ? () => addColumn(activeTable as string) : undefined}
              onRemoveColumn={
                activeTable ? (i) => removeColumn(activeTable as string, i) : undefined
              }
              onLevelPatch={
                activePathKey
                  ? (field, value) => patchLevel(activePathKey as string, { [field]: value } as any)
                  : undefined
              }
              onOpenRulesEditor={
                activeTable
                  ? (colIdx) => {
                      const table = definition.tables?.[activeTable as string];
                      const cols = table?.columns;
                      const col = Array.isArray(cols)
                        ? cols[colIdx]
                        : cols && typeof cols === 'object'
                        ? (cols as any)[Object.keys(cols as any)[colIdx]]
                        : null;
                      const colName = col?.name || (cols && typeof cols === 'object' ? Object.keys(cols as any)[colIdx] : '');
                      if (colName) onOpenRulesEditor(activeTable as string, colName);
                    }
                  : undefined
              }
            />
          </Box>
        </Stack>
      )}
    </Box>
  );
};

export default UnifiedSchemaEditor;
